// ttsUtils.js — ElevenLabs TTS with browser TTS fallback
// Used by PetCompanion and PetVoice

let currentAudio = null; // track playing audio so we can cancel

// Cancel any currently playing TTS
export function cancelTTS() {
  if (currentAudio) {
    currentAudio.pause();
    currentAudio.src = '';
    currentAudio = null;
  }
  try { window.speechSynthesis?.cancel(); } catch {}
}

// Main TTS function — tries ElevenLabs, falls back to browser
// onStart() called when audio begins playing
// onEnd()   called when audio finishes (or on error)
export async function speakWithPet(text, petType, onStart, onEnd) {
  if (!text?.trim()) { onEnd?.(); return; }

  cancelTTS();

  // Try ElevenLabs first
  try {
    const ctrl = new AbortController();
    const timeout = setTimeout(() => ctrl.abort(), 8000);

    const res = await fetch('/api/tts', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      signal:  ctrl.signal,
      body:    JSON.stringify({ text: text.trim(), petType }),
    });
    clearTimeout(timeout);

    if (!res.ok) throw new Error(`TTS ${res.status}`);

    const blob = await res.blob();
    const url  = URL.createObjectURL(blob);
    const audio = new Audio(url);
    currentAudio = audio;

    audio.onplay   = () => onStart?.();
    audio.onended  = () => { URL.revokeObjectURL(url); currentAudio = null; onEnd?.(); };
    audio.onerror  = () => { URL.revokeObjectURL(url); currentAudio = null; onEnd?.(); };

    await audio.play();
    return; // success — done

  } catch (err) {
    // ElevenLabs failed (network, quota, etc.) — fall back to browser TTS
    console.warn('ElevenLabs TTS failed, using browser fallback:', err?.message);
    browserTTSFallback(text, petType, onStart, onEnd);
  }
}

// Browser TTS fallback — timer-based advancement
let browserSeq = 0;
function browserTTSFallback(text, petType, onStart, onEnd) {
  const seq = ++browserSeq;
  const ms  = Math.max(2000, text.trim().split(/\s+/).length * 350);

  onStart?.();

  if (window.speechSynthesis) {
    try {
      window.speechSynthesis.cancel();
      const u     = new SpeechSynthesisUtterance(text);
      const PREFS = {
        lumie:     { pitch:1.9, rate:0.85 },
        cosmo:     { pitch:1.7, rate:0.88 },
        nova:      { pitch:1.8, rate:0.86 },
        neevo:     { pitch:1.7, rate:0.88 },
        luna:      { pitch:1.75, rate:0.86 },
        nova_char: { pitch:1.65, rate:0.89 },
        spark:     { pitch:1.55, rate:0.95 },
        glint:     { pitch:1.72, rate:0.90 },
        orbi:      { pitch:1.45, rate:0.84 },
        zuki:      { pitch:1.60, rate:1.05 },
        echo:      { pitch:1.68, rate:0.87 },
        terra:     { pitch:1.62, rate:0.85 },
      };
      const pref  = PREFS[petType] || { pitch:1.6, rate:0.90 };
      u.pitch     = pref.pitch;
      u.rate      = pref.rate;
      const voices = window.speechSynthesis.getVoices();
      const v = voices.find(x => /samantha|karen|moira|tessa|junior|child/i.test(x.name))
             || voices.find(x => x.lang === 'en-IN')
             || voices.find(x => x.lang.startsWith('en-') && /female|woman/i.test(x.name))
             || voices.find(x => x.lang.startsWith('en'))
             || voices[0];
      if (v) u.voice = v;
      window.speechSynthesis.speak(u);
    } catch {}
  }

  setTimeout(() => {
    if (browserSeq !== seq) return;
    window.speechSynthesis?.cancel();
    onEnd?.();
  }, ms);
}
